/* Luxe Organix — motion.
   Progressive enhancement throughout: content is NEVER hidden unless this
   script runs, and reveals are skipped entirely inside the Shopify theme
   editor. The hero's load choreography is CSS, gated by html.lux-motion from
   an inline <head> script; this file only splits the headline into words for
   it, and CSS has a failsafe if that never happens.

   Pieces, in order:
     1. headline split        (words for the CSS entrance)
     2. scroll reveals        ([data-reveal], and [data-stagger] children)
     3. counters              ([data-lux-count] figures count up once)
     4. spotlight             (pointer-following light on catalogue cards)
     5. hero showcase         (rotating product slides + synced index)
     6. parallax              ([data-parallax], unchanged) */
(function () {
  var design = !!(window.Shopify && window.Shopify.designMode);
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var motion = !design && !reduce && ('IntersectionObserver' in window);

  /* ---------- 1. headline split ----------
     Walks text nodes only, so <em>/<strong>/<a>/<br> inside the inline
     richtext survive untouched. Each word gets --i for its stagger slot; the
     spaces stay real text nodes, so wrapping is exactly what it was. */
  function split(el) {
    var i = 0;
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var parts = node.nodeValue.split(/(\s+)/);
      var frag = document.createDocumentFragment();
      parts.forEach(function (p) {
        if (!p) return;
        if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
        var w = document.createElement('span');
        w.className = 'lux-w';
        w.style.setProperty('--i', i++);
        w.textContent = p;
        frag.appendChild(w);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.classList.add('is-split');
  }
  if (document.documentElement.classList.contains('lux-motion')) {
    document.querySelectorAll('[data-lux-split]').forEach(split);
  }

  /* ---------- 3. counters (defined before reveals, which trigger them) ----------
     Every integer in the figure counts from 0 to its printed value, and the
     final frame writes the ORIGINAL string back verbatim — so formatting,
     separators and any non-numeric text are never altered. Width is locked to
     the final value first, so nothing reflows while it counts. */
  function prepCount(el) {
    var text = el.textContent;
    if (!/\d/.test(text)) return null;
    // A year is a date, not a quantity — "2014" counting up from 0 is absurd.
    if (/^\s*(19|20)\d\d\s*$/.test(text)) return null;
    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = text;
    var vis = document.createElement('span');
    vis.setAttribute('aria-hidden', 'true');
    vis.textContent = text;
    el.textContent = '';
    el.appendChild(sr); el.appendChild(vis);
    vis.style.display = 'inline-block';
    vis.style.minWidth = vis.getBoundingClientRect().width + 'px';
    vis.textContent = text.replace(/\d+/g, '0');
    return function run() {
      var t0 = null, dur = 1400;
      function frame(t) {
        if (t0 === null) t0 = t;
        var k = Math.min(1, (t - t0) / dur);
        var e = 1 - Math.pow(1 - k, 4);
        vis.textContent = k < 1
          ? text.replace(/\d+/g, function (d) { return String(Math.round(+d * e)); })
          : text;
        if (k < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    };
  }

  if (motion) {
    // JS is running & motion allowed → now it's safe to hide-then-reveal
    document.documentElement.classList.add('lux-anim-ready');

    /* ---------- 2. scroll reveals ---------- */
    document.querySelectorAll('[data-stagger]').forEach(function (box) {
      Array.prototype.forEach.call(box.children, function (c, i) {
        c.style.setProperty('--i', Math.min(i, 10));
      });
    });

    var counters = new Map();
    document.querySelectorAll('[data-lux-count]').forEach(function (el) {
      var run = prepCount(el);
      if (run) counters.set(el, run);
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        io.unobserve(el);
        if (counters.has(el)) { counters.get(el)(); return; }
        el.classList.add('lux-in');
        if (el.hasAttribute('data-stagger')) {
          // Hand the children back their own transforms once the last one has
          // landed (10 slots × 70ms + the 1s transition, with margin).
          var n = Math.min(el.children.length, 11);
          setTimeout(function () { el.classList.add('lux-done'); }, n * 70 + 1150);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    document.querySelectorAll('[data-reveal]').forEach(function (el) { io.observe(el); });
    counters.forEach(function (_, el) { io.observe(el); });
  }

  /* ---------- 4. spotlight ---------- */
  if (fine && !reduce) {
    var spots = document.querySelectorAll('.lux-concern, .lux-col, .lux-ing__cell, .lux-specrow');
    spots.forEach(function (el) {
      el.classList.add('lux-spot');
      el.addEventListener('pointermove', function (ev) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--mx', (ev.clientX - r.left) + 'px');
        el.style.setProperty('--my', (ev.clientY - r.top) + 'px');
      }, { passive: true });
    });
  }

  /* ---------- 5. hero showcase ----------
     Pauses (WCAG 2.2.2) on: the pause button (sticky), hover or focus inside
     the hero (temporary — "held"), the hero leaving the viewport, and a hidden
     tab. Never auto-starts under reduced motion or in the theme editor; in the
     editor, selecting a block brings up its slide. */
  document.querySelectorAll('[data-lux-show]').forEach(function (hero) {
    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-lux-slide]'));
    if (slides.length < 2) return;
    var picks = Array.prototype.slice.call(hero.querySelectorAll('[data-lux-pick]'));
    var btn = hero.querySelector('[data-lux-show-pause]');
    var counter = hero.querySelector('[data-lux-show-n]');
    var root = hero.querySelector('[data-lux-show-root]');
    var ms = parseInt(hero.getAttribute('data-interval'), 10) || 6000;
    var cur = 0, timer = null, started = 0, left = ms;
    var paused = reduce || design, held = false, visible = true;

    function pad(n) { return (n < 10 ? '0' : '') + n; }

    function show(i) {
      i = (i + slides.length) % slides.length;
      if (i === cur) return;
      slides[cur].classList.remove('is-active');
      slides[cur].setAttribute('aria-hidden', 'true');
      slides[cur].setAttribute('tabindex', '-1');
      slides[i].classList.add('is-active');
      slides[i].removeAttribute('aria-hidden');
      slides[i].removeAttribute('tabindex');
      cur = i;
      restartProg();
      if (counter) counter.textContent = pad(i + 1);
    }

    // Restart the progress rule: drop the class, force a style flush, re-add.
    function restartProg() {
      picks.forEach(function (p) { p.classList.remove('is-current'); });
      if (picks[cur]) { void picks[cur].offsetWidth; picks[cur].classList.add('is-current'); }
    }

    function running() { return !paused && !held && visible && !document.hidden; }
    function clear() { if (timer) { clearTimeout(timer); timer = null; } }
    function schedule(wait) {
      clear();
      if (!running()) return;
      started = Date.now(); left = wait;
      timer = setTimeout(function () { show(cur + 1); schedule(ms); }, wait);
    }
    function sync() {
      hero.classList.toggle('is-playing', !paused);
      hero.classList.toggle('is-paused', paused);
      hero.classList.toggle('is-held', held || !visible || document.hidden);
      if (running()) { if (!timer) schedule(left); }
      else if (timer) { left = Math.max(0, left - (Date.now() - started)); clear(); }
    }

    if (btn && !reduce && !design) {
      btn.hidden = false;
      btn.addEventListener('click', function () {
        paused = !paused;
        btn.setAttribute('aria-label', paused ? 'Play product rotation' : 'Pause product rotation');
        if (!paused) { left = ms; restartProg(); }
        sync();
      });
    }

    // Hovering or focusing an index card brings its product up. The card is
    // still a link — this only previews; a click navigates.
    picks.forEach(function (p) {
      var i = parseInt(p.getAttribute('data-lux-pick'), 10);
      p.addEventListener('mouseenter', function () { show(i); left = ms; });
      p.addEventListener('focus', function () { show(i); left = ms; });
    });

    // Hover holds the rotation only over the showcase and its index — the
    // places someone is actually reading a product. Resting the cursor on the
    // headline must not freeze the whole hero.
    var index = hero.querySelector('.lux-hero__index');
    [root, index].forEach(function (zone) {
      if (!zone) return;
      zone.addEventListener('mouseenter', function () { held = true; sync(); });
      zone.addEventListener('mouseleave', function () { held = false; sync(); });
    });
    hero.addEventListener('focusin', function () { held = true; sync(); });
    hero.addEventListener('focusout', function (e) {
      if (!hero.contains(e.relatedTarget)) { held = false; sync(); }
    });
    document.addEventListener('visibilitychange', sync);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting; sync();
      }, { threshold: 0.25 }).observe(hero);
    }

    // Pointer tilt: the bottle leans a few pixels toward the cursor. Written
    // as two unitless custom properties on the showcase, consumed by the
    // <img> transform — never a transform on an ancestor (see the CSS: that
    // would break the multiply blend).
    if (root && fine && !reduce) {
      var raf = 0, nx = 0, ny = 0;
      root.addEventListener('pointermove', function (ev) {
        var r = root.getBoundingClientRect();
        nx = ((ev.clientX - r.left) / r.width - .5) * 2;
        ny = ((ev.clientY - r.top) / r.height - .5) * 2;
        if (!raf) raf = requestAnimationFrame(function () {
          raf = 0;
          root.style.setProperty('--tx', nx.toFixed(3));
          root.style.setProperty('--ty', ny.toFixed(3));
        });
      }, { passive: true });
      root.addEventListener('pointerleave', function () {
        root.style.setProperty('--tx', 0); root.style.setProperty('--ty', 0);
      });
    }

    if (design) {
      document.addEventListener('shopify:block:select', function (e) {
        var s = e.target.closest && e.target.closest('[data-lux-slide]');
        if (s && hero.contains(s)) show(parseInt(s.getAttribute('data-lux-slide'), 10));
      });
    }

    sync();
  });

  /* ---------- 6. parallax ----------
     Offset is measured from the element's own position in the viewport, not
     from absolute page scroll. Keying off window.pageYOffset meant the shift
     grew without bound for the whole length of the page — on a long homepage
     the hero image translated hundreds of pixels and slid out of its frame.
     Writes are batched into one rAF so a fast scroll can't thrash layout. */
  if (!motion) return;
  var px = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
  if (!px.length) return;

  var ticking = false;
  var RATE = 0.06;

  function update() {
    ticking = false;
    var vh = window.innerHeight;
    for (var i = 0; i < px.length; i++) {
      var el = px[i];
      // Measure the untransformed parent frame, not the element: reading the
      // element's own rect would feed its current transform back into the next
      // calculation, so the effective rate would drift away from RATE.
      var frame = el.parentElement || el;
      var box = frame.getBoundingClientRect();
      if (box.bottom < 0 || box.top > vh) continue;   // off-screen: nothing to do
      el.style.transform = 'translate3d(0,' + (-box.top * RATE).toFixed(2) + 'px,0)';
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();
